import { getViewer } from './common';
import { getModel } from './model';
import { getNode } from './segmentationTree';

/**
 * @private
 * Calculates the facade visibility score - the percentage of the facade that is visible to the camera
 * @param {Object} facade - facade of interest
 * @param {Object} camera - current camera
 * @param {Object} viewer.viewport - physical hight and width of facade
 * @returns {number} A visibility score from 0 to 1
 */
const getFacadeVisibilityScoreForCamera = (facade, camera) => {
  const { physicalWidth: width, physicalHeight: height } = getViewer().viewport;
  return getModel().computeFacadeVisibilityScore(facade, camera, width, height);
};

/**
 * Is the facade visibility score > 0
 * @param {Object} facade - facade of interest
 * @param {Object} camera - current camera
 * @returns {boolean}
 */
const isFacadeVisibleToCamera = (facade, camera) =>
  getFacadeVisibilityScoreForCamera(facade, camera) > 0.0;

/**
 * Set camera to the calibrated camera
 * @param {Object} camera - current camera
 */
const useCalibratedCamera = (cam) =>
  getModel().setCameraFromCalibratedCamera(getViewer().camera, cam);

/**
 * Calculates the node visibility score - the average percentage of the facades in the node that are visible to the camera
 * @param {Object} nodeRef - node containing all facades of interest
 * @param {Object} camera - current camera
 * @returns {number} A visibility score from 0 to 1
 */
const getNodeVisibilityScoreForCamera = (nodeRef, camera) => {
  const node = getNode(nodeRef);
  const { modelElements: els } = node;
  return (
    els
      .filter((e) => e instanceof Hover.Facade)
      .reduce(
        (acc, e) => acc + getFacadeVisibilityScoreForCamera(e, camera),
        0,
      ) / els.length
  );
};

/**
 * Is the node visibility score > 0
 * @param {Object} nodeRef - node containing all facades of interest
 * @param {Object} camera - current camera
 * @returns {boolean}
 */
const isNodeVisibleToCamera = (nodeRef, camera) => {
  const node = getNode(nodeRef);
  const { modelElements: els } = node;
  return els
    .filter((e) => e instanceof Hover.Facade)
    .some((e) => getFacadeVisibilityScoreForCamera(e, camera) > 0);
};

export default {
  isFacadeVisibleToCamera,
  useCalibratedCamera,
  getNodeVisibilityScoreForCamera,
  isNodeVisibleToCamera,
};
